import { useQuery } from '@tanstack/react-query';
import { useAppSelector } from '@redux/hooks';
import { Employee } from '@models/employee.model';
import { employeesService } from '@services/employees';

/**
 * NOTE: the discriminator is `roster`, not `all`. `useEmployees()` called with
 * no filter builds `['employees', 'all']` for a *paginated* result, so sharing
 * that key would let a `{ employees, total, page, limit }` object land in this
 * hook's cache entry (and vice versa).
 */
export const ALL_EMPLOYEES_QUERY_KEY = (organizationId?: string | null) => [
  'employees',
  'roster',
  organizationId,
];

/**
 * Fetches the full, unpaginated employee roster for the current organization.
 * Prefer this over `useEmployees` when a consumer needs every employee at once
 * (e.g. crew member pickers) — `useEmployees` returns a single paginated page.
 */
export const useAllEmployees = () => {
  const organizationId = useAppSelector(
    (state) => state.organization.organizationId,
  );

  return useQuery<Employee[], Error>({
    queryKey: ALL_EMPLOYEES_QUERY_KEY(organizationId),
    queryFn: () => employeesService.getAllEmployees(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
